import React from 'react';
import { ShieldCheck } from 'lucide-react';
import { TNC_CONTENT } from '@/data/tnc';

/**
 * Formal legal-document renderer for the Match Group Indonesia Terms & Conditions.
 *
 * Rendering conventions (mimicking traditional Indonesian statutory drafting):
 *   - Section title is rendered as "PASAL 1 — TITLE" heading with a thin divider.
 *   - Each item in `paragraphs[]` becomes an "Ayat (1)", "Ayat (2)" numbered clause,
 *     using a hanging-indent layout (Word-style tab: the number stays at the left,
 *     body text wraps under a fixed left margin).
 *   - Sub-lines inside a paragraph that begin with `(a) / (b) / • / 1. / 2.` are
 *     detected line-by-line and rendered with additional left-indent so the outline
 *     reads clearly.
 *   - `highlights[]` are still rendered as a callout box at the end of the pasal.
 */
export default function TncBody({ lang = 'id' }) {
  const items = TNC_CONTENT[lang] || TNC_CONTENT.id;
  const isID = lang !== 'en';

  return (
    <div
      className="space-y-8 t-text-secondary"
      style={{ fontFamily: 'Georgia, "Times New Roman", "Noto Serif", serif' }}
      data-testid="tnc-body"
    >
      <TncHeader lang={lang} />

      {items.map((item, i) => (
        <Pasal key={item.title} item={item} pasalNumber={i + 1} isID={isID} />
      ))}

      <p className="text-[11px] t-text-muted text-center pt-4 border-t t-border">
        {isID
          ? 'Terakhir diperbarui: Juni 2026 · Match Group Indonesia'
          : 'Last updated: June 2026 · Match Group Indonesia'}
      </p>
    </div>
  );
}

function TncHeader({ lang }) {
  const isID = lang !== 'en';
  return (
    <div className="text-center pb-4 border-b t-border">
      <h2 className="t-text font-bold text-lg sm:text-xl" style={{ letterSpacing: '0.02em' }}>
        {isID ? 'SYARAT DAN KETENTUAN' : 'TERMS AND CONDITIONS'}
      </h2>
      <div className="mt-1 text-[11px] t-text-muted italic">
        Match Group Indonesia
      </div>
    </div>
  );
}

function Pasal({ item, pasalNumber, isID }) {
  // Strip leading "N. " that may still be present in the source data.
  const cleanTitle = (item.title || '').replace(/^\s*\d+\s*\.\s*/, '').trim();
  const pasalLabel = isID ? 'PASAL' : 'ARTICLE';

  return (
    <section data-testid={`tnc-item-${pasalNumber}`} className="scroll-mt-16">
      <header className="mb-4">
        <div className="text-[10px] font-bold tracking-[0.28em] text-[#D98A94]">
          {pasalLabel} {toRoman(pasalNumber)}
        </div>
        <h3 className="mt-1 t-text font-semibold text-[15px] sm:text-base uppercase" style={{ letterSpacing: '0.03em' }}>
          {cleanTitle}
        </h3>
        <div className="mt-2 h-px t-border" style={{ background: 'currentColor', opacity: 0.15 }} />
      </header>

      <ol className="space-y-3 list-none">
        {item.paragraphs.map((p, i) => (
          <Ayat key={`${pasalNumber}-ayat-${i}`} text={p} ayatNumber={i + 1} />
        ))}
      </ol>

      {item.highlights?.length > 0 && (
        <div className="mt-4 space-y-2">
          {item.highlights.map((h, hi) => (
            <div
              key={`${pasalNumber}-hl-${hi}`}
              className="flex items-start gap-2 rounded-md p-3"
              style={{ background: 'rgba(217,138,148,0.10)', border: '1px solid rgba(217,138,148,0.30)' }}
            >
              <ShieldCheck className="w-4 h-4 text-[#D98A94] flex-shrink-0 mt-0.5" />
              <p className="text-[12px] sm:text-[13px] font-semibold t-text leading-relaxed">{h}</p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// Sub-line prefix detection patterns (order matters — most specific first).
const LEVEL2_LOWER_RE = /^\s*\(([a-z])\)\s+/;             // (a), (b), (c) ...
const LEVEL2_NUM_RE   = /^\s*(\d{1,2})\.\s+/;             // 1. 2. 3. ...
const LEVEL2_BULLET_RE = /^\s*[•·–—-]\s+/;                // • · – — - bullet prefixes
const LEVEL3_ROMAN_RE  = /^\s*(i|ii|iii|iv|v|vi|vii|viii|ix|x)\.\s+/i;
const SECTION_HEADER_RE = /^\s*\(([A-Z0-9])\)\s+(.+)$/;   // (A), (B), (0), (1) — section header inside an ayat (label hidden)
const KV_LEAD_RE = /^\s*(Interpretasi(?:\s*&\s*posisi)?\s+(?:MGI|Kami)|MGI interpretation|Penjelasan MGI)\s*:\s*/i;

function Ayat({ text, ayatNumber }) {
  const lines = String(text).split('\n');

  // Group lines into semantic blocks: section headers, bullets (with continuations),
  // and paragraph lines. Consecutive lines that continue a bullet (either explicit
  // indent or start with an "Interpretasi MGI:" marker) are folded into that bullet.
  const blocks = [];
  let current = null;
  const flush = () => {
    if (current) {
      blocks.push(current);
      current = null;
    }
  };

  for (const raw of lines) {
    if (!raw.trim()) {
      // Blank line = force a paragraph break.
      flush();
      blocks.push({ type: 'spacer' });
      continue;
    }
    const trimmed = raw.trim();

    // Section header: "(A) Title..." — always its own block.
    const secH = trimmed.match(SECTION_HEADER_RE);
    if (secH) {
      flush();
      blocks.push({ type: 'section', label: `(${secH[1]})`, text: secH[2] });
      continue;
    }

    // Level-2 patterns
    const l2a = trimmed.match(LEVEL2_LOWER_RE);
    const l2n = !l2a && trimmed.match(LEVEL2_NUM_RE);
    const l2b = !l2a && !l2n && LEVEL2_BULLET_RE.test(trimmed);
    const l3  = !l2a && !l2n && !l2b && trimmed.match(LEVEL3_ROMAN_RE);

    if (l2a) {
      flush();
      current = { type: 'bullet', label: `(${l2a[1]})`, lines: [trimmed.replace(LEVEL2_LOWER_RE, '')] };
      continue;
    }
    if (l2n) {
      flush();
      current = { type: 'bullet', label: `${l2n[1]}.`, lines: [trimmed.replace(LEVEL2_NUM_RE, '')] };
      continue;
    }
    if (l2b) {
      flush();
      current = { type: 'bullet', label: '•', lines: [trimmed.replace(LEVEL2_BULLET_RE, '')] };
      continue;
    }
    if (l3) {
      flush();
      current = { type: 'roman', label: `${l3[1].toLowerCase()}.`, lines: [trimmed.replace(LEVEL3_ROMAN_RE, '')] };
      continue;
    }

    // Continuation lines. If the previous block is a bullet/roman and this line is
    // either explicitly indented (>= 3 leading spaces) or starts with a known key
    // marker like "Interpretasi MGI:", fold it into that bullet.
    const isExplicitIndent = /^\s{3,}/.test(raw);
    const isKVMarker = KV_LEAD_RE.test(trimmed);
    if (current && (current.type === 'bullet' || current.type === 'roman') && (isExplicitIndent || isKVMarker)) {
      current.lines.push(trimmed);
      continue;
    }

    // Plain paragraph line.
    flush();
    blocks.push({ type: 'para', text: trimmed });
  }
  flush();

  return (
    <li className="flex items-start gap-3 text-[13px] sm:text-sm leading-[1.75]" style={{ textAlign: 'justify' }}>
      <span className="shrink-0 font-semibold t-text tabular-nums" style={{ minWidth: '2.4rem' }}>
        ({ayatNumber})
      </span>
      <div className="flex-1 space-y-2 min-w-0">
        {blocks.map((b, bi) => {
          const key = `${bi}-${b.type}-${(b.label || b.text || (b.lines && b.lines[0]) || '').slice(0, 20)}`;
          if (b.type === 'spacer') return <div key={key} className="h-1" aria-hidden="true" />;
          if (b.type === 'para') {
            const introLike = /:\s*$/.test(b.text || '');
            const next = blocks[bi + 1];
            const isIntroToBullets = introLike && next && (next.type === 'bullet' || next.type === 'roman');
            return (
              <p
                key={key}
                className={isIntroToBullets ? 't-text pt-1' : ''}
                style={isIntroToBullets ? { marginBottom: '0.15rem' } : undefined}
              >
                {b.text}
              </p>
            );
          }
          if (b.type === 'section') {
            return (
              <div key={key} className="mt-2 first:mt-0">
                <span className="font-semibold t-text">{b.text}</span>
              </div>
            );
          }
          if (b.type === 'bullet' || b.type === 'roman') {
            const indent = b.type === 'roman' ? '2.5rem' : '1.25rem';
            const labelMinWidth = b.label === '•' ? '0.75rem' : (b.label === '–' || b.label === '—' || b.label === '-' ? '0.9rem' : '1.75rem');
            return (
              <div key={key} className="flex items-start gap-2" style={{ paddingLeft: indent }}>
                <span className="shrink-0 tabular-nums t-text" style={{ minWidth: labelMinWidth, fontWeight: 500 }}>
                  {b.label}
                </span>
                <div className="flex-1 min-w-0 space-y-1">
                  {b.lines.map((ln, lni) => {
                    const lineKey = `${key}-ln-${lni}-${ln.slice(0, 16)}`;
                    const kv = ln.match(KV_LEAD_RE);
                    if (kv) {
                      const rest = ln.replace(KV_LEAD_RE, '');
                      return (
                        <p key={lineKey} className="italic t-text-muted">
                          <span className="font-semibold not-italic t-text-secondary">{kv[1]}:</span>{' '}
                          {rest}
                        </p>
                      );
                    }
                    return <p key={lineKey}>{ln}</p>;
                  })}
                </div>
              </div>
            );
          }
          return null;
        })}
      </div>
    </li>
  );
}

function toRoman(n) {
  const map = [
    [1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'],
    [100, 'C'], [90, 'XC'], [50, 'L'], [40, 'XL'],
    [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I'],
  ];
  let out = '';
  let num = n;
  for (const [v, s] of map) {
    while (num >= v) { out += s; num -= v; }
  }
  return out;
}
